import React, { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/i18n/I18nContext";
import { songPriceLabel } from "@/utils/price";
import { PromoBadge } from "@/components/landing/PromoBadge";
import { useIsAndroidApp } from "@/hooks/useLaunchContext";

import SubscriptionPlans from "@/components/subscription/SubscriptionPlans";
import { trackEvent } from "@/utils/analytics";
import { LANDING } from "@/constants/testIds";

export default function PricingSection() {
  const { t, language } = useI18n();
  const navigate = useNavigate();
  const isTwaApp = useIsAndroidApp();

  useEffect(() => {
    trackEvent("view_pricing");
  }, []);

  const included = [
    t("pricing.lyricsAi"), t("pricing.voice"), t("pricing.vocalStylePremium"),
    t("pricing.thumbnail"), t("pricing.customTitle"), t("pricing.download"),
    t("pricing.youtube"), t("pricing.message"), t("pricing.gift"),
    t("pricing.rush"), t("pricing.bonusInstrumental"), t("wizard.socialShortLabel"),
    t("wizard.translateLabel"), t("wizard.certificateLabel"),
    t("pricing.versions2"), t("pricing.versions3"),
  ];

  return (
    <section id="pricing" className="py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="text-center">
          <h2 className="font-heading text-2xl font-medium tracking-tight sm:text-3xl lg:text-4xl">{t("landing.pricingTitle")}</h2>
          <p className="mx-auto mt-3 max-w-xl text-base text-muted-foreground">
            {isTwaApp ? t("landing.pricingSubtitleApp", "Toutes les options et personnalisations incluses.") : t("landing.pricingSubtitle")}
          </p>
        </div>

        <div className="mx-auto mt-6 max-w-xl" />

        {/* Prix unique tout compris — jamais de prix affiché dans l'app Android (Google gère). */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.4 }}
          data-testid={LANDING.pricingCard("flat")}
          className="mx-auto mt-8 max-w-2xl rounded-3xl border-2 border-primary/40 bg-primary/5 p-8 text-center shadow-[0_0_40px_-12px_hsl(var(--primary)/0.5)]"
        >
          <p className="font-heading text-sm font-semibold uppercase tracking-wide text-primary">{t("pricing.flatBadge")}</p>
          {isTwaApp ? (
            <p className="mt-3 text-sm text-muted-foreground">{t("subscription.priceOnPlay")}</p>
          ) : (
            <>
              <p className="mt-3 font-heading text-5xl font-semibold text-primary" data-testid="pricing-flat-price">{songPriceLabel(language)}</p>
              <p className="mt-1 text-sm text-muted-foreground">{t("pricing.flatPerSong")}</p>
              <PromoBadge variant="card" />
            </>
          )}
          <div className="mt-6 grid grid-cols-1 gap-x-6 gap-y-2 text-left sm:grid-cols-2">
            {included.map((label) => (
              <div key={label} className="flex items-center gap-2 text-sm text-muted-foreground">
                <Check className="h-4 w-4 shrink-0 text-primary" />
                {label}
              </div>
            ))}
          </div>
          <Button asChild size="lg" className="mt-8 rounded-full px-8 transition-transform hover:-translate-y-1">
            <Link to="/create">{t("landing.heroCta")}</Link>
          </Button>
        </motion.div>

        {/* Abonnements mensuels (inchangés) */}
        <div className="mt-14" id="subscriptions">
          <h3 className="text-center font-heading text-xl font-semibold">{t("subscription.plansTitle")}</h3>
          <p className="mx-auto mt-2 max-w-md text-center text-sm text-muted-foreground">{t("subscription.plansSubtitle")}</p>
          <div className="mx-auto mt-6 max-w-6xl">
            <SubscriptionPlans onSubscribed={() => navigate("/dashboard")} />
          </div>
        </div>
      </div>
    </section>
  );
}
